import { useId, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogCancel } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Alert } from '@/components/ui/Alert';
import { ApiErrorAlert } from '@/components/ui/ApiErrorAlert';
import {
  createDeviceGroup,
  listSites,
  updateDeviceGroup,
} from '@/lib/api/endpoints/organizations';
import { queryKeys } from '@/lib/query-keys';
import { mergeFieldErrors, trimToNull, type LocalFieldErrors } from '@/lib/form-errors';
import { sitesForOrganization } from './hierarchy';
import type { DeviceGroup, Organization } from '@/lib/api/types';

/*
  Create or edit a device group.

  This is the form the "must not allow an inconsistent parent" requirement is really about. A
  group carries organization_id (required) and site_id (nullable). Two of the three combinations
  are meaningful and one is corrupt:

    org set, site null           an organization wide group. Valid.
    org set, site in that org    a site group. Valid.
    org set, site in another org corrupt, and both foreign keys are individually fine.

  The third is prevented by construction: the organization is fixed by where the dialog was
  opened from, and the site list is only that organization's sites, from sitesForOrganization in
  ./hierarchy.ts. There is no combination of choices in this dialog that produces it. The server
  checks it again anyway, and the mock reproduces the refusal, because a UI-only invariant is one
  refactor away from not being an invariant.

  A group's organization is fixed after creation for the same reason a site's is: assignments
  scoped to the group would silently change what they grant. The site can be changed, because
  the group stays inside the same organization either way and the dialog says what that does.
*/

const ORG_WIDE = '__org_wide__';

export interface DeviceGroupFormDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly organization: Organization;
  /** Null to create, a group to edit. */
  readonly group: DeviceGroup | null;
}

export function DeviceGroupFormDialog({
  open,
  onOpenChange,
  organization,
  group,
}: DeviceGroupFormDialogProps): React.JSX.Element {
  const formId = `group-form-${useId()}`;
  const queryClient = useQueryClient();
  const editing = group !== null;

  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [siteId, setSiteId] = useState<string>(group?.siteId ?? ORG_WIDE);
  const [local, setLocal] = useState<LocalFieldErrors>({});

  const sites = useQuery({
    queryKey: queryKeys.sites(organization.id),
    queryFn: () => listSites(organization.id),
  });

  const [lastOpen, setLastOpen] = useState(open);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) {
      setName(group?.name ?? '');
      setDescription(group?.description ?? '');
      setSiteId(group?.siteId ?? ORG_WIDE);
      setLocal({});
    }
  }

  const allSites = useMemo(() => sites.data ?? [], [sites.data]);
  const siteOptions = useMemo(
    // Only this organization's sites are ever offered. That is the whole enforcement of the
    // parent rule described above.
    () => sitesForOrganization(allSites, organization.id),
    [allSites, organization.id],
  );

  const mutation = useMutation({
    mutationFn: async (): Promise<DeviceGroup> => {
      const payload = {
        siteId: siteId === ORG_WIDE ? null : siteId,
        name: name.trim(),
        description: trimToNull(description),
      };
      if (group === null) {
        return createDeviceGroup({ ...payload, organizationId: organization.id });
      }
      return updateDeviceGroup(group.id, payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['deviceGroups'] });
      void queryClient.invalidateQueries({ queryKey: ['devices'] });
      onOpenChange(false);
    },
  });

  const errors = mergeFieldErrors(local, mutation.error);

  /**
   * Clears the previous failure as soon as a field is edited, so a stale rejection does not sit
   * under a value that has since changed. Same reasoning as OrganizationFormDialog.
   */
  function clearSubmitError(): void {
    if (mutation.isError) {
      mutation.reset();
    }
    if (Object.keys(local).length > 0) {
      setLocal({});
    }
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const found: Record<string, string> = {};
    if (name.trim() === '') {
      found.name = 'A name is required, unique within this organization and site.';
    }
    // Belt and braces against a site list that went stale while the dialog was open.
    if (siteId !== ORG_WIDE && !siteOptions.some((site) => site.id === siteId)) {
      found.siteId = 'That site is no longer part of this organization. Choose another.';
    }
    setLocal(found);
    if (Object.keys(found).length > 0) {
      return;
    }
    mutation.mutate();
  }

  const movingLevel = editing && (group.siteId ?? ORG_WIDE) !== siteId;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? `Edit ${group.name}` : `New device group at ${organization.name}`}
      description="A group collects devices that are administered together. Role assignments and policy can be scoped to one."
      busy={mutation.isPending}
      footer={
        <>
          <DialogCancel disabled={mutation.isPending} />
          <Button type="submit" form={formId} variant="primary" loading={mutation.isPending}>
            {editing ? 'Save changes' : 'Create group'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <p className="text-xs font-semibold tracking-wide text-fg-muted uppercase">
            Organization
          </p>
          <p className="mt-0.5 text-sm text-fg">{organization.name}</p>
          <p className="mt-0.5 text-xs text-fg-subtle">
            Fixed. A group cannot change organization, because every role assignment scoped to it
            would change what it grants without anyone touching the assignment.
          </p>
        </div>

        <Field
          label="Group name"
          required
          autoFocus
          value={name}
          maxLength={120}
          autoComplete="off"
          hint="Operatory Workstations, CNC Controllers, Servers."
          onChange={(event) => {
            clearSubmitError();
            setName(event.target.value);
          }}
          {...(errors.forField('name') === undefined ? {} : { error: errors.forField('name') })}
        />

        <div>
          <Select
            label="Scope within the organization"
            value={siteId}
            options={[
              { value: ORG_WIDE, label: 'Organization wide (any site)' },
              ...siteOptions.map((site) => ({ value: site.id, label: `Site: ${site.name}` })),
            ]}
            onValueChange={setSiteId}
          />
          <p className="mt-1.5 text-xs text-fg-subtle">
            {siteId === ORG_WIDE
              ? 'Devices from any site in this organization can be placed in this group.'
              : 'Only devices at this site can be placed in this group. Narrower scope, so a role assignment on it reaches fewer machines.'}
          </p>
          {errors.forField('siteId') === undefined ? null : (
            <p role="alert" className="mt-1 text-xs font-medium text-[var(--color-danger)]">
              {errors.forField('siteId')}
            </p>
          )}
        </div>

        <Textarea
          label="Description"
          value={description}
          rows={3}
          maxLength={500}
          hint="Optional. Say what the group is for, so the next person does not have to guess from the name."
          onChange={(event) => {
            clearSubmitError();
            setDescription(event.target.value);
          }}
          {...(errors.forField('description') === undefined
            ? {}
            : { error: errors.forField('description') })}
        />

        {siteOptions.length === 0 && !sites.isPending ? (
          <Alert tone="info" title="This organization has no sites yet">
            The group can only be organization wide until a site exists. That is a normal way to
            start; create sites when the customer has more than one location.
          </Alert>
        ) : null}

        {movingLevel ? (
          <Alert tone="warning" title="This changes the group's scope">
            Devices already in the group are not moved, but anyone whose role is scoped to this
            group will now reach a different set of machines. Check the assignments on the
            Technicians page afterwards.
          </Alert>
        ) : null}

        {mutation.isError && !errors.allErrorsAreFieldLevel ? (
          <ApiErrorAlert
            error={mutation.error}
            context={editing ? 'Could not save the group' : 'Could not create the group'}
          />
        ) : null}
      </form>
    </Dialog>
  );
}
